/**
 * Invalida la cache React Query LOCALE appena una mutazione HTTP torna 2xx,
 * senza aspettare il giro dal WebSocket (che serve agli ALTRI client).
 * Stessa idea e stessa deduplicazione di gestionale-gls: il dominio appena
 * invalidato qui viene ignorato per 3 secondi da useRealtimeSync, così chi
 * fa la modifica non rilegge due volte.
 *
 * Le regole rispecchiano realtime/middleware.js del backend. Le presenze
 * cambiano anche il riepilogo dei clienti (copertura, stato del mese) e la
 * panoramica; i dipendenti cambiano scadenze (contratti, documenti) e piano.
 */
import { queryKeys } from "../hooks/queryKeys";
import { emitDomainChanged } from "./domainEvents";

let queryClient = null;
export function registerQueryClient(qc) {
  queryClient = qc;
}

const LOCAL_DEDUP_MS = 3000;
const recentDomains = new Map();

export function isLocallyRecentlyInvalidated(domain) {
  const expiry = recentDomains.get(domain);
  if (!expiry) return false;
  if (Date.now() < expiry) return true;
  recentDomains.delete(domain);
  return false;
}

const PRESENZE = () => [["presenzeMese"], ["clienti"], queryKeys.panoramica, ["dipendentePresenze"], ["dipendente"], ["timbrature"], ["paghe"], ["pagheCliente"]];
const PAGHE = () => [["paghe"], ["pagheCliente"], ["cedolino"], ["pagheCatalogo"], queryKeys.pagheTabelle, queryKeys.panoramica];
const DIPENDENTI = () => [["dipendenti"], ["dipendente"], ["dipendentePresenze"], ["presenzeMese"], ["clienti"], ["scadenze"], queryKeys.panoramica, queryKeys.piano];

// La prima regola che combacia vince: le più specifiche in alto.
const RULES = [
  { re: /\/api\/presenze/i, domain: "presenze", keys: PRESENZE },
  { re: /\/api\/paghe/i, domain: "paghe", keys: PAGHE },
  { re: /\/api\/impostazioni/i, domain: "azienda", keys: () => [queryKeys.impostazioni, ["impostazioniCliente"], ["impostazioniDipendente"], ...PAGHE()] },
  { re: /\/api\/clienti\/\d+\/(impostazioni|terminali|kiosk)/i, domain: "clienti", keys: () => [["impostazioniCliente"], ["cliente"], ["timbrature"], ["pagheCliente"]] },
  { re: /\/api\/dipendenti\/\d+\/(impostazioni|area|pin)/i, domain: "dipendenti", keys: () => [["impostazioniDipendente"], ["dipendente"], ["dipendenti"], ["pagheCliente"]] },
  { re: /\/api\/(timbrature|terminali)/i, domain: "timbrature", keys: () => [["timbrature"], ["impostazioniCliente"], ...PRESENZE()] },
  {
    re: /\/api\/importazioni/i,
    domain: "importazioni",
    keys: () => [queryKeys.importazioni, queryKeys.importProfili, ...PRESENZE(), ["dipendenti"], ["dipendente"], ["clienti"], queryKeys.clientiElenco, queryKeys.pagheTabelle, ["impostazioniDipendente"]],
  },
  { re: /\/api\/clienti\/\d+\/accessi/i, domain: "clienti", keys: () => [["clienti"], queryKeys.clientiElenco, ["cliente"], queryKeys.users, queryKeys.ruoli] },
  { re: /\/api\/clienti/i, domain: "clienti", keys: () => [["clienti"], queryKeys.clientiElenco, ["cliente"], ["presenzeMese"], queryKeys.panoramica, queryKeys.piano] },
  { re: /\/api\/(dipendenti|documenti)/i, domain: "dipendenti", keys: DIPENDENTI },
  { re: /\/api\/scadenze/i, domain: "scadenze", keys: () => [["scadenze"], ["scadenza"], ["dipendente"], queryKeys.panoramica] },
  // Salvataggio automatico delle correzioni nel riepilogo: nessuna rilettura.
  { re: /\/api\/ocr\/documenti\/\d+\/bozza/i, domain: null, keys: () => [] },
  // Il lettore in background chiede se c'è un documento da leggere: non cambia nulla da rileggere.
  { re: /\/api\/ocr\/lettura\/prossimo/i, domain: null, keys: () => [] },
  { re: /\/api\/ocr/i, domain: "ocr", keys: () => [queryKeys.ocrLotti, ["ocrLotto"], queryKeys.ocrStato, ...DIPENDENTI()] },
  { re: /\/api\/notifiche/i, domain: null, keys: () => [queryKeys.notifiche] },
  {
    re: /\/api\/(users|ruoli|aziende\/inviti|aziende\/richieste|me\/azienda)/i,
    domain: "utenti",
    keys: () => [queryKeys.users, queryKeys.people, queryKeys.ruoli, queryKeys.inviti, queryKeys.richieste, ["clienti"], queryKeys.clientiElenco],
  },
  { re: /\/api\/azienda/i, domain: "azienda", keys: () => [queryKeys.azienda, queryKeys.subscription] },
  { re: /\/subscribe/i, domain: "abbonamento", keys: () => [queryKeys.subscription, queryKeys.piano, queryKeys.panoramica] },
  { re: /\/api\/contratto/i, domain: "abbonamento", keys: () => [queryKeys.contratto] },
  { re: /\/api\/email-preferences/i, domain: null, keys: () => [queryKeys.emailPrefs] },
];

const MUTATION_METHODS = new Set(["POST", "PUT", "PATCH", "DELETE"]);
const READ_AS_POST_RE = /(?:^|\/)get[A-Z]/;
const IGNORED_RE = /^\/?(login|register|auth\/|refreshUserToken|report-error|api\/public\/)/i;

export function invalidateForPath(method, url) {
  if (!queryClient || !method || !url) return;
  if (!MUTATION_METHODS.has(method.toUpperCase())) return;
  if (READ_AS_POST_RE.test(url) || IGNORED_RE.test(url.replace(/^https?:\/\/[^/]+/, ""))) return;
  const rule = RULES.find((r) => r.re.test(url));
  if (!rule) {
    if (process.env.NODE_ENV !== "production") {
      console.warn(`[mutationInvalidator] nessuna regola per ${method.toUpperCase()} ${url}`);
    }
    return;
  }
  if (rule.domain) recentDomains.set(rule.domain, Date.now() + LOCAL_DEDUP_MS);
  [...rule.keys(), queryKeys.contatori].forEach((key) => queryClient.invalidateQueries({ queryKey: key, refetchType: "active" }));
  if (rule.domain) emitDomainChanged(rule.domain);
}
